import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { getDeepActiveElement } from './activeElement';
import { pushEscapeLayer } from './escapeLayers';
import { FOCUS_RING } from './focusRing';
import { OVERLAY_Z_BASE, ensureElevatedOverlayStyles } from './portalOverlay';
import { lockBodyScroll } from './scrollLock';
import { leaveTopLayer, raiseToTopLayer } from './topLayer';

/** Back/close button chrome, shared so the two overlays cannot drift apart. */
export const OVERLAY_ICON_BUTTON_CLASSES = `flex shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-[4px] text-[var(--malachite-text-secondary)] hover:text-[var(--malachite-text-primary)] transition-colors ${FOCUS_RING}`;

export const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type PanelSource = () => HTMLElement | null;

// LIFO like escapeLayers: with a listener per overlay the first-registered one wins, which
// sent Tab from a dialog into the panel behind it.
const trapLayers: PanelSource[] = [];
// Elevation belongs to the stack: a Modal left in the base band opens behind the panel that
// elevated it.
const elevatedLayers = new Set<PanelSource>();
// The shadow host per layer, so promotion can replay in stack order.
const hostSources = new Map<PanelSource, () => HTMLElement | null>();
const stackListeners = new Set<() => void>();
const NO_SUBSCRIPTION = () => {
    // Nothing to tear down: a closed overlay never subscribed.
};

// Top-layer paint order is entry order, so replay the whole stack bottom-up rather than letting
// each overlay promote itself: those effects run in React tree order, which need not match.
let promoted: HTMLElement[] = [];

const syncTopLayer = (): void => {
    const wanted = elevatedLayers.size
        ? trapLayers
              .map((layer) => hostSources.get(layer)?.())
              .filter((host): host is HTMLElement => Boolean(host))
        : [];
    // Compared, not reapplied: this runs on every commit, because a host can appear a commit
    // after the layer that owns it, and re-entering the top layer unprompted costs a recalc.
    if (
        wanted.length === promoted.length &&
        wanted.every((host, index) => host === promoted[index])
    ) {
        return;
    }
    const stillWanted = new Set(wanted);
    for (const host of promoted) {
        if (!stillWanted.has(host)) {
            leaveTopLayer(host);
        }
    }
    for (const host of wanted) {
        raiseToTopLayer(host);
    }
    promoted = wanted;
};

const notifyStackChanged = (): void => {
    for (const listener of stackListeners) {
        listener();
    }
};

const subscribeToStack = (listener: () => void): (() => void) => {
    stackListeners.add(listener);
    return () => {
        stackListeners.delete(listener);
    };
};

const handleTabKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab' || event.defaultPrevented) {
        return;
    }
    // Walk down rather than bail: React detaches refs before flushing the cleanup that pops
    // the layer, so the topmost can be registered-but-null and Tab would escape entirely.
    let panel: HTMLElement | null = null;
    for (let index = trapLayers.length - 1; index >= 0 && !panel; index -= 1) {
        panel = trapLayers[index]();
    }
    if (!panel) {
        return;
    }
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = getDeepActiveElement();
    const activeInPanel = active !== null && panel.contains(active);
    if (event.shiftKey) {
        if (!activeInPanel || active === first || active === panel) {
            event.preventDefault();
            last.focus();
        }
    } else if (!activeInPanel || active === last) {
        event.preventDefault();
        first.focus();
    }
};

// On `document` so it also catches focus that escaped behind the overlay. The trap reaches
// focusables in the overlay's own shadow root only, so a popup portaled elsewhere (Dropdown,
// ActionMenu, Tooltip) is outside it and Tab will pull focus back out of the popup.
const pushFocusTrapLayer = (getPanel: PanelSource, getHost: () => HTMLElement | null) => {
    if (trapLayers.length === 0) {
        document.addEventListener('keydown', handleTabKeyDown);
    }
    trapLayers.push(getPanel);
    hostSources.set(getPanel, getHost);
    notifyStackChanged();
    return () => {
        const index = trapLayers.lastIndexOf(getPanel);
        if (index !== -1) {
            trapLayers.splice(index, 1);
        }
        elevatedLayers.delete(getPanel);
        hostSources.delete(getPanel);
        const host = getHost();
        if (host) {
            leaveTopLayer(host);
        }
        if (trapLayers.length === 0) {
            document.removeEventListener('keydown', handleTabKeyDown);
        }
        notifyStackChanged();
    };
};

/**
 * Whether a SidePanel or Modal is open anywhere, for host chrome that has to move out of the way.
 * Overlays portal, so the only other signal is this module's markers, which a host cannot read
 * without coupling to an internal. Dropdowns and tooltips do not count: they trap no focus.
 */
export const useHasOpenOverlay = (): boolean =>
    useSyncExternalStore(
        subscribeToStack,
        () => trapLayers.length > 0,
        () => false,
    );

/**
 * The modal-overlay behaviour shared by SidePanel and Modal: ESC and the Tab focus trap
 * (topmost overlay only, so these may be stacked), background scroll lock, and focus moved
 * into the overlay on open then restored to the trigger on close.
 *
 * Returns the ref callback the dialog element must take, whether this overlay is the topmost
 * one — only that one may claim `aria-modal`, or a screen reader can still browse the overlay
 * underneath — the z-index its host must carry, which keeps paint order with the focus stack,
 * and whether it is elevated. Presentation otherwise stays with the calling component.
 */
export const useOverlayBehaviour = ({
    open,
    onClose,
    elevated = false,
    hostRef,
}: {
    open: boolean;
    onClose: () => void;
    elevated?: boolean;
    /** The shadow host, which the stack promotes into the top layer while elevated. */
    hostRef?: { current: HTMLElement | null };
}): {
    ref: (node: HTMLElement | null) => void;
    isTopmost: boolean;
    zIndex: number | undefined;
    elevated: boolean;
} => {
    const panelRef = useRef<HTMLElement | null>(null);
    // One identity for the life of the instance: elevation registers it in the layout phase,
    // before the effect below pushes it onto the stack.
    const source = useRef<PanelSource>(() => panelRef.current).current;
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const layerRef = useRef<PanelSource | null>(null);

    // Ref so the ESC layer registers once per open, not on every inline-onClose change.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    // Before the trap effect, so the trigger is recorded before focus moves in — and relies on
    // ShadowRootWrapper portaling a commit late, or this would capture the panel itself.
    useEffect(() => {
        if (!open) {
            return;
        }
        previousFocusRef.current = getDeepActiveElement();
        return () => {
            const previous = previousFocusRef.current;
            previousFocusRef.current = null;
            if (previous?.isConnected) {
                previous.focus();
            }
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        layerRef.current = source;
        const popEscapeLayer = pushEscapeLayer(() => onCloseRef.current());
        const releaseScrollLock = lockBodyScroll();
        const popFocusTrapLayer = pushFocusTrapLayer(source, () => hostRef?.current ?? null);
        // Reopening inside the exit transition reuses the same node, so the ref callback
        // never fires again and nothing would take focus.
        panelRef.current?.focus();
        return () => {
            layerRef.current = null;
            popFocusTrapLayer();
            popEscapeLayer();
            releaseScrollLock();
        };
    }, [open, hostRef, source]);

    // Its own effect: re-pushing the trap layer to change elevation would move this overlay to
    // the top of the stack, handing it the Escape and Tab that belong to a dialog above it.
    // Layout, and declared above the sync below, so that sync sees it in the same commit.
    useLayoutEffect(() => {
        if (!open || !elevated) {
            return;
        }
        elevatedLayers.add(source);
        notifyStackChanged();
        return () => {
            elevatedLayers.delete(source);
            notifyStackChanged();
        };
    }, [open, elevated, source]);

    // Only an open overlay subscribes: a mounted-but-idle one must carry no at-rest cost,
    // and its snapshot is a constant `false` regardless of what the stack does.
    const subscribe = useCallback(
        (onStackChange: () => void) => (open ? subscribeToStack(onStackChange) : NO_SUBSCRIPTION),
        [open],
    );

    const isTopmost = useSyncExternalStore(
        subscribe,
        // Gated on `open` so an overlay fading out stops claiming modal. Before it registers
        // it is the one about to go on top, or aria-modal would drop for a frame on open.
        () =>
            open &&
            (layerRef.current === null || trapLayers[trapLayers.length - 1] === layerRef.current),
        () => true,
    );

    // Its own request counts before the stack has it: the layer is not pushed until effects run.
    const stackElevated = useSyncExternalStore(
        subscribe,
        () => open && (elevated || elevatedLayers.size > 0),
        () => false,
    );

    // Overlay hosts share one z-index band, so paint order would otherwise fall out of body
    // order: an overlay reopened over a newer one takes the keyboard while painting under it.
    const zIndex = useSyncExternalStore(
        subscribe,
        () => {
            const layer = layerRef.current;
            return OVERLAY_Z_BASE + (open && layer ? Math.max(0, trapLayers.indexOf(layer)) : 0);
        },
        () => OVERLAY_Z_BASE,
    );

    // Layout, not passive: the rule has to exist before the first paint the attribute applies to.
    useLayoutEffect(() => {
        if (stackElevated) {
            ensureElevatedOverlayStyles();
        }
    }, [stackElevated]);

    // No deps: the host mounts a commit after the layer that owns it, so there is no stack
    // change left to key this on. `syncTopLayer` compares before it touches anything.
    useLayoutEffect(() => {
        syncTopLayer();
    });

    const ref = useCallback((node: HTMLElement | null) => {
        panelRef.current = node;
        // The panel mounts fresh on every open, so first attach = time to take focus.
        node?.focus();
    }, []);

    return { ref, isTopmost, zIndex: stackElevated ? undefined : zIndex, elevated: stackElevated };
};

export default useOverlayBehaviour;
